import { useCallback, useRef } from "react";

type Operation<T = any> = {
  id: string;
  type: "add" | "remove" | "update";
  execute: () => Promise<T>;
};

type ChainedRequestsState = {
  isProcessing: boolean;
  queue: Operation[];
  currentOperation: Operation | null;
};

type Params = {
  onRequestsFinished?: () => void;
};

export const useChainRequests = (params?: Params) => {
  const { onRequestsFinished } = params || {};
  const state = useRef<ChainedRequestsState>({
    isProcessing: false,
    queue: [],
    currentOperation: null,
  });

  const processQueue = useCallback(async () => {
    if (state.current.isProcessing || state.current.queue.length === 0) {
      return;
    }

    state.current.isProcessing = true;

    while (state.current.queue.length > 0) {
      const operation = state.current.queue.shift()!;
      state.current.currentOperation = operation;

      try {
        await operation.execute();
      } catch (error) {
        console.error(
          `Operation ${operation.id} (${operation.type}) failed:`,
          error,
        );
      }

      state.current.currentOperation = null;
    }

    state.current.isProcessing = false;

    if (onRequestsFinished) {
      onRequestsFinished();
    }
  }, [onRequestsFinished]);

  const addToPromiseChain = useCallback(
    (operation: Operation) => {
      // An in-flight removal wins over a queued update for the same item.
      if (
        state.current.currentOperation?.id === operation.id &&
        state.current.currentOperation.type === "remove" &&
        operation.type === "update"
      ) {
        return;
      }

      // Check if there's a conflicting operation in the queue
      const existingIndex = state.current.queue.findIndex(
        (op) => op.id === operation.id,
      );

      if (existingIndex !== -1) {
        const existingOp = state.current.queue[existingIndex];

        if (existingOp.type === operation.type) {
          state.current.queue[existingIndex] = operation;
          return;
        }

        if (
          (existingOp.type === "add" && operation.type === "remove") ||
          (existingOp.type === "remove" && operation.type === "add")
        ) {
          state.current.queue.splice(existingIndex, 1);
          return;
        }

        // A queued removal always wins over an update for the same item —
        // there's nothing left to update once it's gone.
        if (existingOp.type === "remove" && operation.type === "update") {
          return;
        }
        if (existingOp.type === "update" && operation.type === "remove") {
          state.current.queue[existingIndex] = operation;
          return;
        }
      }

      state.current.queue.push(operation);

      processQueue();
    },
    [processQueue],
  );

  const isOperationPending = useCallback((operationId: string) => {
    return (
      state.current.queue.some((op) => op.id === operationId) ||
      state.current.currentOperation?.id === operationId
    );
  }, []);

  const getOperationStatus = useCallback((operationId: string) => {
    const queuedOperation = state.current.queue.find(
      (op) => op.id === operationId,
    );
    if (queuedOperation) {
      return { status: "queued", type: queuedOperation.type };
    }

    if (state.current.currentOperation?.id === operationId) {
      return {
        status: "processing",
        type: state.current.currentOperation.type,
      };
    }

    return { status: "idle", type: null };
  }, []);

  const clearQueue = useCallback(() => {
    state.current.queue = [];
  }, []);

  return {
    addToPromiseChain,
    isOperationPending,
    getOperationStatus,
    clearQueue,
    get isProcessing() {
      return state.current.isProcessing;
    },
    get queueLength() {
      return state.current.queue.length;
    },
  };
};
